/**
 * anime.js v4 — curated agent primer.
 *
 * Surfaced to the director LLM when the user asks for text animation,
 * SVG morphing, or overlay motion. Keep this file ~150 lines so it fits in a
 * single context block. Source of truth: https://animejs.com/documentation/
 *
 * What anime.js v4 offers (vs the prior v3 API):
 *   - Composable engine functions: createAnimation, createTimeline, createDraggable,
 *     createScroll, animate, stagger, utils, eases
 *   - Timeline.seek(ms) lets us drive frames deterministically (used by the
 *     studio renderer at currentTime - clip.startAt)
 *   - Tween targets can be DOM nodes OR plain JS objects (we use the latter
 *     for canvas-driven rendering — no DOM thrash)
 *
 * IMPORTANT: PreviewCanvas reads target values back every frame instead of
 * letting anime mutate DOM. So pass plain `{}` targets and have anime mutate
 * fields like `{ x, y, opacity, scale, rotate, blur }`.
 */

export const ANIME_VERSION = '4.x';

export const ANIME_PRESETS = {
  /** Each character fades + rises 40px → 0px, staggered by `stagger` ms. */
  'stagger-in':    { splitBy: 'char', stagger: 40, duration: 600, easing: 'easeOutQuad' },
  /** Per-character opacity ramp — used as the anime.js equivalent of the legacy typewriter. */
  'typewriter-anime': { splitBy: 'char', stagger: 50, duration: 30, easing: 'linear' },
  /** Sine-y vertical ripple while fading in. */
  'wave':          { splitBy: 'char', stagger: 60, duration: 700, easing: 'easeInOutSine' },
  /** Scale 0.5 → 1.1 → 1.0, opacity in. */
  'zoom-pulse':    { splitBy: 'word', stagger: 80, duration: 700, easing: 'easeOutBack' },
  /** RGB-shift inspired horizontal jitter + rotate jitter. */
  'glitch':        { splitBy: 'char', stagger: 35, duration: 500, easing: 'easeOutQuad' },
  /** Blur-in shimmer; great for music intros. */
  'shimmer':       { splitBy: 'char', stagger: 70, duration: 800, easing: 'easeInOutQuad' },
} as const;

/** System-prompt addendum the director can paste in when the user is asking
 *  for text or SVG animation. Returns a markdown block. */
export function buildAnimeAgentPrompt(): string {
  return `
ANIME.JS V4 (text / SVG animation overlay):

When the user asks for animated text overlays beyond the built-in "fade",
"typewriter", "slide-up", emit a clip with:
  { type: 'text', animationStyle: 'anime', animeSpec: {...} }

animeSpec keys:
  preset?: 'stagger-in' | 'typewriter-anime' | 'wave' | 'zoom-pulse' | 'glitch' | 'shimmer'
  splitBy?: 'char' | 'word' | 'line'        // how to chunk targets
  stagger?: number                          // ms between target animations
  duration?: number                         // ms per target
  easing?: string                           // anime.js v4 easing: 'linear', 'easeOutQuad',
                                            //   'easeInOutSine', 'easeOutBack', 'inOutCubic', ...
  loop?: boolean
  font?: { family?, weight?, sizePx? }
  raw?: Record<string, unknown>             // advanced: passed straight to anime.js timeline

Choosing a preset:
  • title cards / hero text          → 'stagger-in' or 'zoom-pulse'
  • intro-style reveal               → 'typewriter-anime'
  • music / dance content            → 'wave' or 'shimmer'
  • cyberpunk / glitch aesthetic     → 'glitch'
  • dreamy / soft                    → 'shimmer'

Easing cheatsheet (anime.js v4):
  linear · easeInQuad · easeOutQuad · easeInOutQuad
  easeOutBack (overshoots — good for impact)
  easeInOutSine (smooth — good for loops)
  spring(mass, stiffness, damping, velocity)   // physics-based

For raw timeline ops (advanced), example:
  raw: {
    targets: '$char',                        // we expand $char to per-character objects
    keyframes: [
      { y: -20, opacity: 0, duration: 0 },
      { y: 0,   opacity: 1, duration: 400 }
    ],
    delay: anime.stagger(40),
    ease: 'easeOutQuad'
  }

For SVG animation:
  Pass an SVG path string + an animeSpec targeting 'svgMorph'. (Roadmap.)
`;
}

/** When the chat agent decides text needs anime treatment, this helper turns
 *  a free-form description into a structured animeSpec. */
export function inferAnimeSpec(description: string): { preset: keyof typeof ANIME_PRESETS; splitBy: 'char' | 'word' | 'line' } {
  const d = description.toLowerCase();
  if (/glitch|cyber|broken|distort/.test(d))                       return { preset: 'glitch',         splitBy: 'char' };
  if (/wave|float|water|hover|breathe/.test(d))                    return { preset: 'wave',           splitBy: 'char' };
  if (/shimmer|dream|soft|blur|hazy/.test(d))                      return { preset: 'shimmer',        splitBy: 'char' };
  if (/pulse|pop|punch|impact|bold/.test(d))                       return { preset: 'zoom-pulse',     splitBy: 'word' };
  if (/typewriter|typing|reveal char/.test(d))                     return { preset: 'typewriter-anime', splitBy: 'char' };
  return { preset: 'stagger-in', splitBy: 'char' };
}
